
import { SimulationConfig, AppSave } from '../types';

// Assumes backend endpoints:
// GET /api/saves
// POST /api/saves (body: { name, config, isDefault })
// PUT /api/saves/:id (body: { name, isDefault })
// DELETE /api/saves/:id
// POST /api/upload (form-data: file), returns { url: string }

export class CloudService {
  private static API_BASE = '/api';

  static async getSaves(): Promise<AppSave[]> {
    try {
      const res = await fetch(`${this.API_BASE}/saves`);
      if (!res.ok) throw new Error('Failed to fetch saves');
      const data = await res.json();
      return data.map((s: any) => ({
        ...s,
        config: typeof s.config === 'string' ? JSON.parse(s.config) : s.config
      }));
    } catch (e) {
      console.warn("CloudService: Offline or API not found.", e);
      return [];
    }
  }

  static async saveConfig(name: string, config: SimulationConfig, isDefault: boolean = false, melodyId?: string, soundIds?: string[]): Promise<AppSave | null> {
    try {
      // Strip heavy data from config before sending
      const cleanConfig = { ...config };
      // @ts-ignore
      delete cleanConfig.customMelodies;
      // @ts-ignore
      delete cleanConfig.userSounds;

      const res = await fetch(`${this.API_BASE}/saves`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, config: cleanConfig, isDefault, melodyId, soundIds })
      });
      if (!res.ok) throw new Error('Save failed');
      return await res.json();
    } catch (e) {
      console.error("Cloud save failed", e);
      return null;
    }
  }

  static async registerMelody(name: string, data: any): Promise<any | null> {
    try {
      const res = await fetch(`${this.API_BASE}/melodies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: data.id, name, data })
      });
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async getMelodies(): Promise<any[]> {
    try {
      const res = await fetch(`${this.API_BASE}/melodies`);
      return await res.json();
    } catch (e) {
      return [];
    }
  }

  static async updateSave(id: string, updates: { name?: string, isDefault?: boolean, config?: SimulationConfig }): Promise<boolean> {
    try {
      const body: any = { ...updates };
      if (body.config) {
          const cleanConfig = { ...body.config };
          // @ts-ignore
          delete cleanConfig.customMelodies;
          // @ts-ignore
          delete cleanConfig.userSounds;
          body.config = cleanConfig;
      }

      const res = await fetch(`${this.API_BASE}/saves/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async deleteSave(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.API_BASE}/saves/${id}`, { method: 'DELETE' });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  // Sounds
  static async getSounds(): Promise<any[]> {
    try {
      const res = await fetch(`${this.API_BASE}/sounds`);
      if (!res.ok) throw new Error('Failed to fetch sounds');
      return await res.json();
    } catch (e) {
      return [];
    }
  }

  static async registerSound(name: string, url: string, id?: string): Promise<any | null> {
    try {
      const res = await fetch(`${this.API_BASE}/sounds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, data: url, id }) // Changed 'url' to 'data' to match backend schema
      });
      if (!res.ok) throw new Error('Sound registration failed');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async registerSoundData(name: string, data: string): Promise<any | null> {
      return this.registerSound(name, data);
  }

  static async deleteSound(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.API_BASE}/sounds/${id}`, { method: 'DELETE' });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  // Songs
  static async getSongs(): Promise<any[]> {
    try {
      const res = await fetch(`${this.API_BASE}/songs`);
      if (!res.ok) throw new Error('Failed to fetch songs');
      return await res.json();
    } catch (e) {
      return [];
    }
  }

  static async registerSong(name: string, data: string, id?: string): Promise<any | null> {
    try {
      const res = await fetch(`${this.API_BASE}/songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, data })
      });
      if (!res.ok) throw new Error('Song registration failed');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async getConvertedSongs(): Promise<any[]> {
    try {
      const res = await fetch(`${this.API_BASE}/converted_songs`);
      if (!res.ok) throw new Error('Failed to fetch converted songs');
      return await res.json();
    } catch (e) {
      return [];
    }
  }

  static async registerConvertedSong(name: string, data: string): Promise<any | null> {
    try {
      const res = await fetch(`${this.API_BASE}/converted_songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, data })
      });
      if (!res.ok) throw new Error('Converted song registration failed');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async deleteSong(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.API_BASE}/songs/${id}`, { method: 'DELETE' });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async deleteMelody(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.API_BASE}/melodies/${id}`, { method: 'DELETE' });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async uploadFile(file: File): Promise<string | null> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${this.API_BASE}/upload`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return data.url; // Expecting { url: "https://r2-bucket..." }
    } catch (e) {
      console.warn("Cloud upload failed, falling back to local.", e);
      return null;
    }
  }
}
